import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calculator, FileText, RefreshCw, Save, Shield, Users, Folder, Building2 } from 'lucide-react';

export default function TechniqueTab({ 
  technique, 
  setTechnique, 
  intervenants: intervenants, 
  etudes, 
  setEtudes, 
  savingDraft, 
  handleSaveDraft, 
  saving, 
  handleSaveSimulation, 
  setActiveTab 
}) {
  return (
    <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-emerald-600" />
                    Caractéristiques techniques de l'opération
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Coût de construction (€) *</Label>
                      <Input
                        type="number"
                        value={technique.cout_construction_ht}
                        onChange={(e) => setTechnique(prev => ({ ...prev, cout_construction_ht: parseFloat(e.target.value) || 0 }))}
                        className="mt-1"
                        min={30000}
                        data-testid="input-cout-construction"
                      />
                      <p className="text-xs text-gray-500 mt-1">Minimum : 30 000 €</p>
                    </div>
                    <div>
                      <Label>Surface plancher autorisé (m²) *</Label>
                      <Input
                        type="number"
                        value={technique.surface_plancher}
                        onChange={(e) => setTechnique(prev => ({ ...prev, surface_plancher: parseFloat(e.target.value) || 0 }))}
                        className="mt-1"
                        min={1}
                      />
                    </div>
                  </div>
                  
                  <Separator className="my-4" />
                  <h4 className="font-medium text-gray-700">Nombre de bâtiments et logements</h4>
                  
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <Label>Nombre de bâtiments</Label>
                      <Input
                        type="number"
                        value={technique.nombre_batiments}
                        onChange={(e) => setTechnique(prev => ({ ...prev, nombre_batiments: parseInt(e.target.value) || 1 }))}
                        className="mt-1"
                        min={1}
                      />
                    </div>
                    <div>
                      <Label>Nombre de logements</Label>
                      <Input
                        type="number"
                        value={technique.nombre_logements}
                        onChange={(e) => setTechnique(prev => ({ ...prev, nombre_logements: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                        min={0}
                      />
                    </div>
                    <div>
                      <Label>Locaux professionnels</Label>
                      <Input
                        type="number"
                        value={technique.nombre_locaux_professionnels}
                        onChange={(e) => setTechnique(prev => ({ ...prev, nombre_locaux_professionnels: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                        min={0}
                      />
                    </div>
                    <div>
                      <Label>Durée travaux (mois)</Label>
                      <Input
                        type="number"
                        value={technique.duree_travaux_mois}
                        onChange={(e) => setTechnique(prev => ({ ...prev, duree_travaux_mois: parseInt(e.target.value) || 12 }))}
                        className="mt-1"
                        min={1}
                        max={60}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Nombre d'étages</Label>
                      <Input
                        type="number"
                        value={technique.nombre_niveaux}
                        onChange={(e) => setTechnique(prev => ({ ...prev, nombre_niveaux: parseInt(e.target.value) || 1 }))}
                        className="mt-1"
                        min={1}
                      />
                    </div>
                    <div>
                      <Label>Nombre de sous-sols</Label>
                      <Input
                        type="number"
                        value={technique.nombre_sous_sols}
                        onChange={(e) => setTechnique(prev => ({ ...prev, nombre_sous_sols: parseInt(e.target.value) || 0 }))}
                        className="mt-1"
                        min={0}
                      />
                    </div>
                    <div>
                      <Label>Date début travaux</Label>
                      <DatePickerFr
                        className="mt-1 w-full"
                        value={technique.date_debut_travaux}
                        onChange={(v) => setTechnique(prev => ({ ...prev, date_debut_travaux: v }))}
                      />
                    </div>
                  </div>
                  
                  <Separator className="my-4" />
                  <h4 className="font-medium text-gray-700">Structure et fondations</h4>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Type de structure</Label>
                      <Select 
                        value={technique.type_structure} 
                        onValueChange={(v) => setTechnique(prev => ({ ...prev, type_structure: v }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beton">Béton</SelectItem>
                          <SelectItem value="bois">Bois</SelectItem>
                          <SelectItem value="mixte">Mixte</SelectItem>
                          <SelectItem value="acier">Acier</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Type de fondations</Label>
                      <Select 
                        value={technique.type_fondations} 
                        onValueChange={(v) => setTechnique(prev => ({ ...prev, type_fondations: v }))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="superficielles">Superficielles</SelectItem>
                          <SelectItem value="profondes">Profondes</SelectItem>
                          <SelectItem value="speciales">Spéciales</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="font-medium">Présence de piscine</div>
                        <div className="text-sm text-gray-500">Indiquez si le projet comprend une piscine</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={technique.presence_piscine}
                        onCheckedChange={(checked) => setTechnique(prev => ({ ...prev, presence_piscine: checked }))}
                      />
                      {technique.presence_piscine && (
                        <Input
                          type="number"
                          value={technique.surface_piscine}
                          onChange={(e) => setTechnique(prev => ({ ...prev, surface_piscine: e.target.value }))}
                          placeholder="Surface (m²)"
                          className="w-32"
                        />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Études et Maîtrise d'œuvre */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-emerald-600" />
                    Études et Maîtrise d'œuvre
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-medium">Étude de sol réalisée</div>
                        <Input
                          value={etudes.nom_bureau_etude_sol}
                          onChange={(e) => setEtudes(prev => ({ ...prev, nom_bureau_etude_sol: e.target.value }))}
                          placeholder="Nom du bureau d'étude"
                          className="mt-1 w-64"
                          disabled={!etudes.etude_sol_realisee}
                        />
                      </div>
                    </div>
                    <Switch
                      checked={etudes.etude_sol_realisee}
                      onCheckedChange={(checked) => setEtudes(prev => ({ ...prev, etude_sol_realisee: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-medium">Étude d'assainissement</div>
                        <Input
                          value={etudes.nom_bureau_assainissement}
                          onChange={(e) => setEtudes(prev => ({ ...prev, nom_bureau_assainissement: e.target.value }))}
                          placeholder="Nom du bureau"
                          className="mt-1 w-64"
                          disabled={!etudes.etude_assainissement}
                        />
                      </div>
                    </div>
                    <Switch
                      checked={etudes.etude_assainissement}
                      onCheckedChange={(checked) => setEtudes(prev => ({ ...prev, etude_assainissement: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-medium">Maître d'œuvre ou Architecte</div>
                        <div className="flex gap-2 mt-1">
                          <Input
                            value={etudes.nom_architecte}
                            onChange={(e) => setEtudes(prev => ({ ...prev, nom_architecte: e.target.value }))}
                            placeholder="Nom de l'architecte"
                            className="w-48"
                            disabled={!etudes.maitre_oeuvre_architecte}
                          />
                          <Input
                            value={etudes.numero_ordre_architecte}
                            onChange={(e) => setEtudes(prev => ({ ...prev, numero_ordre_architecte: e.target.value }))}
                            placeholder="N° Ordre"
                            className="w-32"
                            disabled={!etudes.maitre_oeuvre_architecte}
                          />
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={etudes.maitre_oeuvre_architecte}
                      onCheckedChange={(checked) => setEtudes(prev => ({ ...prev, maitre_oeuvre_architecte: checked }))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div>
                        <div className="font-medium">Bureau d'études techniques (structure)</div>
                        <Input
                          value={etudes.nom_bureau_etudes_structure}
                          onChange={(e) => setEtudes(prev => ({ ...prev, nom_bureau_etudes_structure: e.target.value }))}
                          placeholder="Nom du bureau d'études"
                          className="mt-1 w-64"
                          disabled={!etudes.bureau_etudes_techniques}
                        />
                      </div>
                    </div>
                    <Switch
                      checked={etudes.bureau_etudes_techniques}
                      onCheckedChange={(checked) => setEtudes(prev => ({ ...prev, bureau_etudes_techniques: checked }))}
                    />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5 text-emerald-600" />
                    Réductions solidaires
                  </CardTitle>
                  <CardDescription>
                    Options permettant de réduire votre contribution
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-emerald-600" />
                      <div>
                        <div className="font-medium">Mesures de prévention</div>
                        <div className="text-sm text-gray-500">Réduction de 5% si mesures de prévention intégrées</div>
                      </div>
                    </div>
                    <Switch
                      checked={technique.mesures_prevention}
                      onCheckedChange={(checked) => setTechnique(prev => ({ ...prev, mesures_prevention: checked }))}
                      data-testid="switch-prevention"
                    />
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-xl space-y-3">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="font-medium">Part des entreprises ESS</div>
                        <div className="text-sm text-gray-500">Jusqu'à 10% de réduction si travaux réalisés par des entreprises ESS</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Input
                        type="range"
                        min="0"
                        max="100"
                        value={technique.part_entreprises_ess}
                        onChange={(e) => setTechnique(prev => ({ ...prev, part_entreprises_ess: parseInt(e.target.value) }))}
                        className="flex-1"
                      />
                      <span className="font-bold text-blue-600 w-12 text-right">{technique.part_entreprises_ess}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex justify-between gap-3">
                <Button variant="outline" onClick={() => setActiveTab('operation')}>
                  Retour
                </Button>
                <div className="flex gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => handleSaveDraft()}
                    disabled={savingDraft}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    data-testid="btn-save-draft"
                  >
                    {savingDraft ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Folder className="h-4 w-4 mr-2" />
                    )}
                    Sauvegarder brouillon (30j)
                  </Button>
                  <Button 
                    onClick={handleSaveSimulation} 
                    disabled={saving}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="btn-save-simulation"
                  >
                    {saving ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Créer la souscription
                  </Button>
                </div>
              </div>
    </div>
  );
}
